import { truncate } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Text } from '~/eds/src/components/Text/Text';
import { Table } from '~/eds/src/modules/Table/Table';
import { StatusType } from '~/eds/src/types/StatusType';
import { scrollElementIntoView } from '~/eds/src/utils/scrollElementIntoView';
import { ClauseType } from '~/enums/ClauseType';
import { api } from '~/redux/api/createApi';
import { DocumentClause } from '~/types/Clause';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { copyToClipboard } from '~/utils/helper.utils';

import styles from './ClausesTable.styles';
import useEditClauseModal from './useEditClauseModal/useEditClauseModal';
import useShowMoreClauseModal from './useShowMoreClauseModal/useShowMoreClauseModal';

type Props = {
  handlerId: number;
  clauseIdToScroll?: number;
};

type PaginationParams = {
  pageIndex: number;
};

const defaultClause: DocumentClause = {
  id: 0,
  name: '',
  content: '',
  title: '',
  version: '',
  clauseType: '',
  clauseVersion: '',
  htmlTokens: [],
  coordinates: [],
  isLast: false,
  isEdited: false,
};

const PAGE_SIZE = 10;

const ClausesTable = ({ handlerId, clauseIdToScroll }: Props) => {
  const intl = useIntl();
  const [clause, setClause] = useState<DocumentClause>(defaultClause);
  const [hasShownClauseToScroll, setHasShownClauseToScroll] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isFetching } = api.endpoints.getAllClauses.useQuery({
    docId: handlerId,
    page,
  });

  const [deleteDocumentClause, deleteDocumentClauseResult] =
    api.endpoints.deleteDocumentClause.useMutation();
  const { isError, isSuccess, isLoading } = deleteDocumentClauseResult;

  useEffect(() => {
    if (isSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ClausesTable.deleteProvisionSuccess',
            defaultMessage: '{clauseName} Clause is permanently deleted',
            description: 'Success message when a provision is deleted',
          },
          { clauseName: clause.name },
        ),
      );
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'ClausesTable.deleteClauseError',
          defaultMessage: 'Something went wrong, please try again later.',
          description: 'Error message when deleting a clause fails',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    if (data && clauseIdToScroll && !hasShownClauseToScroll) {
      const isInCurrentPage = data.results.find(
        (el) => el.id === clauseIdToScroll,
      );
      if (isInCurrentPage) {
        scrollElementIntoView(clauseIdToScroll.toString(), { block: 'center' });
        setHasShownClauseToScroll(true);
      } else {
        const hasMorePages = page * PAGE_SIZE < data.count;
        if (hasMorePages) {
          setPage((page) => page + 1);
        }
      }
    }
  }, [data, clauseIdToScroll]);

  const clearClause = () => {
    setClause(defaultClause);
  };

  const [clauseModal, showClauseModal] = useShowMoreClauseModal({
    content: clause.content,
    title: clause.title,
    onClose: clearClause,
  });
  const [editModal, showEditModal] = useEditClauseModal(
    clause,
    handlerId,
    clearClause,
  );

  const renderClauseContent = (cell: DocumentClause) => {
    return (
      <Box styles={styles.tableCell}>
        <Text id={`${cell.id}`} variant="body">
          {truncate(cell.content, { length: 500, omission: '...' })}
        </Text>
        {cell.content.length > 500 ? (
          <Button
            onClick={() => {
              setClause(cell);
              showClauseModal();
            }}
            variant="action"
            text={intl.formatMessage({
              id: 'ClausesTable.readMore',
              defaultMessage: 'Read More',
              description: 'Button text for reading more of a clause',
            })}
          />
        ) : null}
      </Box>
    );
  };

  const columns = [
    {
      key: 'name',
      title: intl.formatMessage({
        id: 'ClausesTable.clauseColumnTitle',
        defaultMessage: 'Clause',
        description: 'Column title for clause name',
      }),
      width: 'm',
      disableSortBy: true,
      disableResizing: true,
      renderCell: (cell: DocumentClause) => {
        return (
          <Text variant="body-bold" styles={styles.clauseTitle}>
            {cell.name}
          </Text>
        );
      },
    },
    {
      key: 'content',
      title: intl.formatMessage({
        id: 'ClausesTable.contentColumnTitle',
        defaultMessage: 'Content',
        description: 'Column title for clause content',
      }),
      disableSortBy: true,
      disableResizing: true,
      width: 'auto',
      renderCell: renderClauseContent,
    },
    {
      key: 'version',
      title: intl.formatMessage({
        id: 'ClausesTable.documentVersionColumnTitle',
        defaultMessage: 'Document Version',
        description: 'Column title for document version',
      }),
      width: 'm',
      disableSortBy: true,
      disableResizing: true,
      cellType: 'text',
      mapCellProps: ({ version }: DocumentClause) => ({ text: version }),
    },
    {
      key: 'clauseVersion',
      title: intl.formatMessage({
        id: 'ClausesTable.clauseVersionColumnTitle',
        defaultMessage: 'Clause Version',
        description: 'Column title for clause version',
      }),
      width: 'm',
      disableSortBy: true,
      disableResizing: true,
      renderCell: (cell: DocumentClause) => {
        return (
          <Box styles={styles.versionContainer}>
            <Text textAlign="right" variant="body">
              {cell.clauseVersion}
            </Text>
            {cell.isEdited && (
              <Text variant="tiny" color="text.secondary">
                {intl.formatMessage({
                  id: 'ClausesTable.editedLabel',
                  defaultMessage: 'Edited',
                  description: 'Label for edited clause version',
                })}
              </Text>
            )}
          </Box>
        );
      },
    },
    {
      key: 'source',
      title: intl.formatMessage({
        id: 'ClausesTable.sourceColumnTitle',
        defaultMessage: 'Source',
        description: 'Column title for clause source',
      }),
      width: 'm',
      disableSortBy: true,
      disableResizing: true,
      renderCell: (cell: DocumentClause) => {
        let status: StatusType | undefined = undefined;
        switch (cell.clauseType) {
          case ClauseType.EvisortAI:
            status = 'info';
            break;
          case ClauseType.QuickAI:
            status = 'inactive';
            break;
          case ClauseType.Migrated:
            status = 'warning';
            break;
        }
        return <Chip status={status} text={cell.clauseType} />;
      },
    },
  ];

  const rowActions = [
    {
      label: intl.formatMessage({
        id: 'ClausesTable.copyToClipboardAction',
        defaultMessage: 'Copy to clipboard',
        description: 'Row action label for copying clause content to clipboard',
      }),
      onClick: (row: DocumentClause) => {
        copyToClipboard(
          row.content,
          intl.formatMessage({
            id: 'ClausesTable.clausesCopiedToast',
            defaultMessage: 'Clause content copied to clipboard',
            description: 'Toast message when clause content is copied',
          }),
          SUCCESS,
          null,
          true,
        );
      },
    },
    {
      label: intl.formatMessage({
        id: 'ClausesTable.editClauseAction',
        defaultMessage: 'Edit clause',
        description: 'Row action label for editing a clause',
      }),
      onClick: (row: DocumentClause) => {
        setClause(row);
        showEditModal();
      },
    },
    {
      label: intl.formatMessage({
        id: 'ClausesTable.deleteClauseAction',
        defaultMessage: 'Delete clause',
        description: 'Row action label for deleting a clause',
      }),
      onClick: (row: DocumentClause) => {
        deleteDocumentClause({
          clauseId: row.id,
          documentHandlerId: handlerId,
        });
      },
    },
  ];

  return (
    <>
      {/* @ts-ignore: Disable the table component props shape verification*/}
      <Table
        name={intl.formatMessage({
          id: 'ClausesTable.tableName',
          defaultMessage: 'Clauses',
          description: 'Name for clauses table',
        })}
        data={data?.results || []}
        columns={columns}
        totalCount={data?.count}
        rowActions={rowActions}
        isBordered={false}
        state={{ pageIndex: page, pageSize: PAGE_SIZE }}
        isLoading={isFetching || isLoading}
        onPaginate={({ pageIndex }: PaginationParams) => {
          setPage(pageIndex);
        }}
        options={{
          enableExportXlsx: false,
          enableSelectRows: false,
          enableManageColumns: false,
        }}
      />
      {clauseModal}
      {editModal}
    </>
  );
};

export default ClausesTable;
